import { faEnvelope, faLocationDot, faPhone } from '@fortawesome/free-solid-svg-icons'
import { fab, type IconName } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library, type IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { getTranslations } from 'next-intl/server'
import {
  ContactInformationBlock as ContactInformationBlockTypes,
  GeneralInformation,
} from '@/payload-types'
import { Media } from '@onclicks/sharedcomponents/Media/Media'
import RichText from '../components/RichText'

library.add(fab)

type SocialLink = NonNullable<
  NonNullable<
    NonNullable<GeneralInformation['socialNetworks']>[number]['social']
  >[number]['linkWithMedia']
>

const formatPhoneNumber = (phone: string) =>
  phone.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')

// Même résolution que le footer : URL custom, sinon ancre ou slug de la référence
const getSocialHref = (link?: SocialLink | null) => {
  if (link?.url) return link.url

  const value = link?.reference?.value
  if (value && typeof value === 'object') {
    if ('anchor' in value && value.anchor) return `#${value.anchor}`
    if ('slug' in value && value.slug) return `/${value.slug}`
  }
  return null
}

const ContactItem: React.FC<{
  icon: IconDefinition
  label: string
  href: string
  external?: boolean
  children: React.ReactNode
}> = ({ icon, label, href, external, children }) => (
  <a
    href={href}
    {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    className="group flex items-center gap-4"
  >
    <span className="flex size-11 shrink-0 items-center justify-center rounded-full border border-primary text-primary transition-colors group-hover:bg-primary group-hover:text-white">
      <FontAwesomeIcon icon={icon} className="size-4" aria-hidden="true" />
    </span>
    <span className="flex flex-col">
      <span className="text-xs uppercase tracking-widest text-white/60">{label}</span>
      <span className="text-white group-hover:text-primary transition-colors">{children}</span>
    </span>
  </a>
)

const ContactInformationBlock: React.FC<ContactInformationBlockTypes> = async (props) => {
  const { title, description, email, phone, addressesBlocks, socialNetworks } = props || {}
  const t = await getTranslations('web_contact')

  const socialNetworksData = (socialNetworks as GeneralInformation)?.socialNetworks?.[0]
  const addresses = addressesBlocks?.flatMap((block) => block.addresses ?? []) ?? []

  return (
    <div className="flex h-full flex-col gap-8 rounded-lg bg-linear-to-b from-[#2A2D3F] via-[#0A0A0E] to-black p-8 text-white shadow-2xl">
      {title && (
        <h2 className="font-bold underline underline-offset-8 decoration-primary">{title}</h2>
      )}

      {description && (
        <RichText
          data={description}
          enableGutter={false}
          enableProse={false}
          className="text-white/80"
        />
      )}

      <div className="flex flex-col gap-6">
        {email && (
          <ContactItem icon={faEnvelope} label={t('by_email')} href={`mailto:${email}`}>
            {email}
          </ContactItem>
        )}

        {phone && (
          <ContactItem icon={faPhone} label={t('by_phone')} href={`tel:${phone}`}>
            {formatPhoneNumber(phone)}
          </ContactItem>
        )}

        {addresses.map((address, index) => (
          <ContactItem
            key={address.id ?? index}
            icon={faLocationDot}
            label={address.titleAddress || t('address')}
            href={address.googleLink || '#'}
            external={!!address.googleLink}
          >
            <p>
              {address.street} {address.number}
              {address.box ? `/${address.box}` : ''},
            </p>
            <p></p>
            <p>
              {address.postalCode} {address.locality}
            </p>
            <p>{address.country}</p>
          </ContactItem>
        ))}
      </div>

      {socialNetworksData?.social && socialNetworksData.social.length > 0 && (
        <div className="border-t border-primary/30 pt-6">
          <p className="mb-4 text-xs uppercase tracking-widest text-white/60">
            {t('social_networks')}
          </p>
          <div className="flex flex-wrap gap-3">
            {socialNetworksData.social.map((socialMedia, index) => {
              const link = socialMedia.linkWithMedia
              const href = getSocialHref(link)
              const media = link?.media && typeof link.media === 'object' ? link.media : null
              // kms7 configure ses réseaux avec une image ; l'icône Font Awesome n'est qu'un repli
              const iconName = link?.reactIconsIcon || socialMedia.reactIconsIcon

              if (!href || (!media && !iconName)) return null

              return (
                <a
                  key={socialMedia.id ?? index}
                  href={href}
                  target={link?.newTab ? '_blank' : undefined}
                  rel={link?.newTab ? 'noopener noreferrer' : undefined}
                  aria-label={link?.label || media?.alt || media?.filename || t('social_networks')}
                  className="flex size-11 items-center justify-center transition-opacity hover:opacity-75"
                >
                  {media ? (
                    <Media resource={media} imgClassName="size-10 object-contain" loading="lazy" />
                  ) : (
                    <FontAwesomeIcon
                      icon={['fab', iconName as IconName]}
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  )}
                </a>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export { ContactInformationBlock }
